import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getPricingSettings,
  updatePricingSettings,
} from "../../services/apiService";
import {
  CLIENT_PRICING_TIER_OPTIONS,
  getPricingTierLabel,
  PRICING_TEMPLATE_FIELDS,
  syncDefaultPricingFields,
} from "../../utils/pricingTemplates";
import WorkbenchShell from "../workbench/WorkbenchShell";
import { StatusCell } from "../workbench/WorkbenchStatusBar";
import PricingImpactDialog, {
  ChangedConstantsList,
  PricingImpactTable,
  usePricingImpact,
} from "./PricingImpactDialog";
import {
  extractRateConstants,
  rateConstantsDiffer,
  REFERENCE_PERCENT_FIELDS,
} from "./referenceQuote";
import "./adminShell.css";
import "./PricingSettings.css";

const DEFAULT_LABOR_BURDEN_RATE = 36;
const DEFAULT_ISLAND_MILLWORK_LABOR_RATE = 90;

/**
 * The three-way labor-rate-type selector was retired 2026-08-18 (owner
 * ratified): the burden rate IS the labor cost per employee-hour the engine
 * bills hours at, and the Island Millwork rate is a client charge-out
 * reference, not an engine input. `labor_rate_type` and `labor_custom_rate`
 * stay in the settings payload so saving this screen cannot blank a column the
 * engine still reads for legacy JobVersions — but neither is editable here,
 * and the mode is pinned to burden.
 */
const buildInitialSettings = () =>
  syncDefaultPricingFields({
    labor_rate_type: "burden",
    labor_custom_rate: 30,
    labor_burden_rate: DEFAULT_LABOR_BURDEN_RATE,
    island_millwork_labor_rate: DEFAULT_ISLAND_MILLWORK_LABOR_RATE,
    default_client_pricing_tier: "retail",
  });

const LABOR_SECTION_ID = "pricing-section-labor";
const TIERS_SECTION_ID = "pricing-section-tiers";
const tierSectionId = (tier) => `pricing-section-tier-${tier}`;

/** How long the ambient inspector waits after a keystroke before re-pricing. */
const AMBIENT_DELAY_MS = 600;

/**
 * Admin-only component for managing global pricing factors.
 * Labor defaults stay global. Client-facing pricing defaults now live in
 * templates by tier.
 *
 * The one behavioural addition over the old screen: before any of the nine
 * constants the calculator reads is saved, the admin sees the same reference
 * job priced both ways. Nothing on this screen derives a price itself — every
 * dollar in the comparison comes back from `/api/calculate`.
 */
function PricingSettings() {
  const [settings, setSettings] = useState(buildInitialSettings);
  // What loadSettings() last returned — the "Now" column's rates.
  const [savedSettings, setSavedSettings] = useState(buildInitialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [impactOpen, setImpactOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(LABOR_SECTION_ID);
  const centerRef = useRef(null);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPricingSettings();
      const next = syncDefaultPricingFields({
        ...data,
        // A row saved before the retirement keeps whatever mode it stored (the
        // engine still honors it); a row with nothing stored means burden, not
        // the old $30 custom fallback.
        labor_rate_type: data?.labor_rate_type || "burden",
        labor_burden_rate: data?.labor_burden_rate ?? DEFAULT_LABOR_BURDEN_RATE,
        island_millwork_labor_rate:
          data?.island_millwork_labor_rate ??
          DEFAULT_ISLAND_MILLWORK_LABOR_RATE,
      });
      setSettings(next);
      setSavedSettings(next);
    } catch (err) {
      setError("Failed to load pricing settings");
      console.error("Load settings error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((previous) => {
      const nextSettings = {
        ...previous,
        [field]: field === "labor_rate_type" ? value : parseFloat(value) || 0,
      };

      if (field === "default_client_pricing_tier") {
        return syncDefaultPricingFields({
          ...nextSettings,
          default_client_pricing_tier: value,
        });
      }

      return nextSettings;
    });
    setSuccess(null);
  };

  const handleTemplateChange = (tier, field, value) => {
    const parsedValue = parseFloat(value) || 0;
    setSettings((previous) => {
      const nextSettings = {
        ...previous,
        pricing_templates: {
          ...previous.pricing_templates,
          [tier]: {
            ...previous.pricing_templates?.[tier],
            [field]: parsedValue,
          },
        },
      };

      if (tier === previous.default_client_pricing_tier) {
        return syncDefaultPricingFields(nextSettings);
      }

      return nextSettings;
    });
    setSuccess(null);
  };

  /** The exact save the old screen performed — unchanged, just callable twice. */
  const persistSettings = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = syncDefaultPricingFields(settings);
      await updatePricingSettings(payload);

      setSuccess(
        `Pricing settings updated successfully. New estimates will default to the ${getPricingTierLabel(
          payload.default_client_pricing_tier,
        )} template.`,
      );

      await loadSettings();
    } catch (err) {
      setError(err.message || "Failed to save pricing settings");
      console.error("Save settings error:", err);
    } finally {
      setSaving(false);
    }
  }, [settings]);

  const pendingSettings = useMemo(
    () => syncDefaultPricingFields(settings),
    [settings],
  );
  const savedConstants = useMemo(
    () => extractRateConstants(savedSettings),
    [savedSettings],
  );
  const pendingConstants = useMemo(
    () => extractRateConstants(pendingSettings),
    [pendingSettings],
  );
  const constantsChanged = useMemo(
    () => rateConstantsDiffer(savedConstants, pendingConstants),
    [savedConstants, pendingConstants],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    // A changed constant moves real quotes — show what it does first.
    if (constantsChanged) {
      setImpactOpen(true);
      return;
    }
    await persistSettings();
  };

  const handleConfirmImpact = async () => {
    await persistSettings();
    setImpactOpen(false);
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const node = centerRef.current?.querySelector(`#${id}`);
    if (node?.scrollIntoView) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return (
      <WorkbenchShell
        variant="admin"
        title="Pricing settings"
        center={<p className="wb-empty">Loading pricing settings...</p>}
      />
    );
  }

  const railNav = (
    <nav className="pricing-rail" aria-label="Pricing settings sections">
      <SectionLink
        id={LABOR_SECTION_ID}
        label="Labor defaults"
        active={activeSection === LABOR_SECTION_ID}
        onSelect={scrollToSection}
      />
      <SectionLink
        id={TIERS_SECTION_ID}
        label="Client tier defaults"
        active={activeSection === TIERS_SECTION_ID}
        onSelect={scrollToSection}
      />
      <div className="pricing-rail__group">
        {CLIENT_PRICING_TIER_OPTIONS.map((option) => (
          <SectionLink
            key={option.value}
            id={tierSectionId(option.value)}
            label={option.label}
            meta={
              savedSettings.default_client_pricing_tier === option.value
                ? "Default"
                : null
            }
            active={activeSection === tierSectionId(option.value)}
            indent
            onSelect={scrollToSection}
          />
        ))}
      </div>
    </nav>
  );

  const defaultTemplate =
    savedSettings.pricing_templates?.[
      savedSettings.default_client_pricing_tier
    ] || {};

  const statusBar = (
    <>
      <StatusCell
        label="Default tier"
        value={getPricingTierLabel(savedSettings.default_client_pricing_tier)}
      />
      {REFERENCE_PERCENT_FIELDS.map((key) => {
        const field = PRICING_TEMPLATE_FIELDS.find((item) => item.key === key);
        return (
          <StatusCell
            key={key}
            label={field.label.replace(/\s*\(%\)\s*$/, "")}
            value={`${defaultTemplate[key] ?? 0}%`}
            title={field.hint}
          />
        );
      })}
    </>
  );

  return (
    <>
      <WorkbenchShell
        variant="admin"
        title="Pricing settings"
        subtitle="Global defaults"
        meta={
          constantsChanged ? (
            <span className="wb-pill">Unsaved rate changes</span>
          ) : null
        }
        actions={
          <>
            <button
              type="button"
              className="wb-tbtn"
              onClick={loadSettings}
              disabled={saving}
            >
              Reset
            </button>
            <button
              type="button"
              className="wb-tbtn wb-tbtn--solid"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </>
        }
        railHeader={{ label: "Sections" }}
        rail={railNav}
        centerHeader={{ label: "Global pricing defaults" }}
        center={
          <div className="pricing-settings-container" ref={centerRef}>
            <p className="settings-description">
              Labor defaults stay global. Client pricing defaults are controlled
              by tier templates, and the selected default tier seeds every new
              estimate.
            </p>

            {error && <div className="alert alert-error">{error}</div>}

            {success && <div className="alert alert-success">{success}</div>}

            <form onSubmit={handleSubmit} className="pricing-settings-form">
              <section
                className="pricing-settings-section"
                id={LABOR_SECTION_ID}
              >
                <h3>Labor Defaults</h3>
                <div className="form-group">
                  <label htmlFor="labor_burden_rate">
                    Burden Rate ($/hr)
                    <span className="field-hint">
                      The labor COST per employee-hour. Every shop hour on every
                      estimate is billed at this rate — it is the engine&apos;s
                      only labor rate input.
                    </span>
                  </label>
                  <input
                    id="labor_burden_rate"
                    type="number"
                    step="0.5"
                    min="0"
                    value={
                      settings.labor_burden_rate ?? DEFAULT_LABOR_BURDEN_RATE
                    }
                    onChange={(event) =>
                      handleChange("labor_burden_rate", event.target.value)
                    }
                    onFocus={(event) => event.target.select()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="island_millwork_labor_rate">
                    Island Millwork Rate ($/hr)
                    <span className="field-hint">
                      Reference client charge-out rate per hour. Kept for
                      quoting conversations — it is NOT used in engine math.
                    </span>
                  </label>
                  <input
                    id="island_millwork_labor_rate"
                    type="number"
                    step="0.5"
                    min="0"
                    value={
                      settings.island_millwork_labor_rate ??
                      DEFAULT_ISLAND_MILLWORK_LABOR_RATE
                    }
                    onChange={(event) =>
                      handleChange(
                        "island_millwork_labor_rate",
                        event.target.value,
                      )
                    }
                    onFocus={(event) => event.target.select()}
                    required
                  />
                </div>
              </section>

              <section
                className="pricing-settings-section"
                id={TIERS_SECTION_ID}
              >
                <h3>Client Tier Defaults</h3>
                <div className="form-group">
                  <label htmlFor="default_client_pricing_tier">
                    Default Client Pricing Tier
                    <span className="field-hint">
                      New estimates start on this tier, and estimators can
                      switch tiers per estimate.
                    </span>
                  </label>
                  <select
                    id="default_client_pricing_tier"
                    value={settings.default_client_pricing_tier || "retail"}
                    onChange={(event) =>
                      handleChange(
                        "default_client_pricing_tier",
                        event.target.value,
                      )
                    }
                    required
                  >
                    {CLIENT_PRICING_TIER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pricing-template-grid">
                  {CLIENT_PRICING_TIER_OPTIONS.map((tierOption) => {
                    const template =
                      settings.pricing_templates?.[tierOption.value] || {};
                    const isDefaultTier =
                      settings.default_client_pricing_tier === tierOption.value;

                    return (
                      <section
                        key={tierOption.value}
                        id={tierSectionId(tierOption.value)}
                        className={`pricing-template-card${
                          isDefaultTier ? " pricing-template-card--default" : ""
                        }`}
                      >
                        <div className="pricing-template-card__header">
                          <div>
                            <h4>{tierOption.label}</h4>
                            <p>
                              {isDefaultTier
                                ? "Used to seed every new estimate."
                                : "Available to estimators from the pricing modal."}
                            </p>
                          </div>
                          {isDefaultTier && (
                            <span className="pricing-template-card__badge">
                              Default
                            </span>
                          )}
                        </div>

                        <div className="pricing-template-card__fields">
                          {PRICING_TEMPLATE_FIELDS.map((field) => (
                            <div
                              className="form-group"
                              key={`${tierOption.value}-${field.key}`}
                            >
                              <label
                                htmlFor={`${tierOption.value}-${field.key}`}
                              >
                                {field.label}
                                <span className="field-hint">{field.hint}</span>
                              </label>
                              <input
                                id={`${tierOption.value}-${field.key}`}
                                data-testid={`${tierOption.value}-${field.key.replace(
                                  /_ratio$|_margin$|_rate$/,
                                  "",
                                )}`}
                                type="number"
                                min={field.min}
                                max={field.max}
                                step={field.step}
                                value={template[field.key] ?? 0}
                                onChange={(event) =>
                                  handleTemplateChange(
                                    tierOption.value,
                                    field.key,
                                    event.target.value,
                                  )
                                }
                                onFocus={(event) => event.target.select()}
                                required
                              />
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </section>

              {/* The top bar carries the same pair; this one keeps the form
                  submittable from the keyboard and reachable at the bottom of
                  a long scroll. Same handlers, same copy. */}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={loadSettings}
                  disabled={saving}
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        }
        inspectorHeader={{ label: "Quote impact" }}
        inspector={
          <PricingImpactInspector
            savedConstants={savedConstants}
            pendingConstants={pendingConstants}
            changed={constantsChanged}
          />
        }
        statusBar={statusBar}
      />

      <PricingImpactDialog
        open={impactOpen}
        oldConstants={savedConstants}
        newConstants={pendingConstants}
        saving={saving}
        onConfirm={handleConfirmImpact}
        onCancel={() => setImpactOpen(false)}
      />
    </>
  );
}

function SectionLink({ id, label, meta, active, indent, onSelect }) {
  return (
    <button
      type="button"
      className={`wb-item pricing-rail__item${
        active ? " wb-item--on" : ""
      }${indent ? " pricing-rail__item--indent" : ""}`}
      onClick={() => onSelect(id)}
    >
      <span className="wb-item__t">
        <span>{label}</span>
      </span>
      {meta ? <span className="wb-item__m">{meta}</span> : null}
    </button>
  );
}

/**
 * The ambient view of the same comparison the save gate shows. Debounced so a
 * keystroke does not fire a calculator run, and idle until something actually
 * differs from what is saved.
 */
function PricingImpactInspector({ savedConstants, pendingConstants, changed }) {
  const { loading, error, before, after } = usePricingImpact({
    oldConstants: savedConstants,
    newConstants: pendingConstants,
    active: changed,
    delay: AMBIENT_DELAY_MS,
  });

  if (!changed) {
    return (
      <div className="pi-inspector">
        <p className="pi-idle">
          These are the rates as saved. Change one and this pane prices the same
          reference job both ways, so you can see what it does to a quote before
          you save.
        </p>
      </div>
    );
  }

  return (
    <div className="pi-inspector">
      <div className="pi-changes-block">
        <h4 className="pi-changes__hd">What you changed</h4>
        <ChangedConstantsList
          before={savedConstants}
          after={pendingConstants}
        />
      </div>

      {loading && !before ? (
        <p className="pi-status" role="status">
          Pricing the reference job both ways…
        </p>
      ) : null}

      {error ? (
        <p className="pi-status pi-status--error" role="alert">
          Could not run the comparison — {error}
        </p>
      ) : null}

      {before && after ? (
        <PricingImpactTable before={before} after={after} />
      ) : null}
    </div>
  );
}

export default PricingSettings;
